import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { resolveTiersV2, resolveFinishRateV2, sqft, formatDims, roomSqftBreakdown, roomsSqftBreakdown, formatSqftBreakdown } from "@/lib/quotationApi";
import { hexToRgb } from "@/lib/branding";
import { exportPublicSnapshotPDF } from "@/lib/pdfExportSnapshot";

const DEFAULT_GOLD = [197, 168, 128];
const DARK = [30, 33, 45];
const MUTED = [120, 125, 140];
const PREMIUM = [154, 184, 216];
const ECONOMY = [143, 178, 144];

// Print-safe (B&W) palette — high-contrast greys engineered for laser printers.
const BW = {
  GOLD: [40, 40, 40], // accent → near-black
  DARK: [10, 10, 10],
  MUTED: [110, 110, 110],
  // Soft cream backgrounds → very light neutral grey (still prints clean)
  HEAD_FILL: [238, 238, 238],
  ALT_ROW_FILL: [248, 248, 248],
  GRAND_BLOCK_FILL: [250, 250, 250],
  GRAND_TEXT: [40, 40, 40],
  GRAND_VALUE: [10, 10, 10],
  HEAD_TEXT: [60, 60, 60],
  ROW_TEXT: [40, 40, 40],
  LINK: [60, 60, 60],
  TIER_FALLBACK: [
    [10, 10, 10], // tier 0 (hero)
    [70, 70, 70], // tier 1
    [120, 120, 120], // tier 2
    [150, 150, 150], // tier 3+
  ],
};

// Map a coloured tier to a grayscale shade based on its index (so each tier
// stays visually distinct on a B&W printout).
function tierColorBW(index) {
  return BW.TIER_FALLBACK[Math.min(index, BW.TIER_FALLBACK.length - 1)];
}

const rupee = (n) =>
  "Rs. " + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

async function urlToDataUrl(url) {
  try {
    const resp = await fetch(url, { credentials: "omit" });
    if (!resp.ok) return null;
    const blob = await resp.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function fileExtToPdfFormat(dataUrl) {
  if (!dataUrl) return "PNG";
  const m = dataUrl.match(/^data:image\/(\w+);/i);
  const t = (m && m[1] ? m[1] : "png").toUpperCase();
  if (t === "JPG" || t === "JPEG") return "JPEG";
  if (t === "WEBP") return "WEBP";
  return "PNG";
}

export async function exportQuotationPDF(data, savedId, shareUrl, tierSettings, brandingOpts) {
  const brandingInfo = brandingOpts?.branding || {};
  const monochrome = Boolean(brandingOpts?.monochrome);
  const fileSuffix = brandingOpts?.fileSuffix || (monochrome ? "_print" : "");
  const firmName = (brandingInfo.firm_name || "LIVANA").toUpperCase();
  const tagline = brandingInfo.tagline || "INTERIOR QUOTATION";
  const baseGold = brandingInfo.primary_color ? hexToRgb(brandingInfo.primary_color) : DEFAULT_GOLD;
  const GOLD = monochrome ? BW.GOLD : baseGold;
  const tierAccent = (color, index) =>
    monochrome ? tierColorBW(index) : hexToRgb(color);
  const logoUrl = brandingOpts?.logoUrl || "";
  const logoData = logoUrl ? await urlToDataUrl(logoUrl) : null;
  const showPayment = Boolean(brandingInfo.show_payment_info);
  const qrStoragePath = (brandingInfo.qr_storage_path || "").trim();
  const qrBaseUrl = (brandingOpts?.apiBase || "").replace(/\/$/, "");
  const qrUrl = showPayment && qrStoragePath && qrBaseUrl ? `${qrBaseUrl}/files/${qrStoragePath}` : "";
  const qrData = qrUrl ? await urlToDataUrl(qrUrl) : null;

  // Resolve tiers to v2 list (from snapshot on `data.tiers` OR live config).
  const tierOpts = tierSettings || {};
  const tieredV2 = resolveTiersV2(
    data,
    data?.tiers,
    tierOpts.tierConfigV2 || null,
    tierOpts.selectedTierIds || (data?.tiers && data.tiers.selected_tier_ids) || null,
  );
  const tierList = tieredV2.list || [];
  // Display order for tier columns/cards: highest grand total → lowest.
  // `tierList` (configured order) is retained so finish-cascade lookups
  // continue to walk toward the base tier correctly.
  const sortedTierList = [...tierList].sort(
    (a, z) => (z.breakdown?.grand_total || 0) - (a.breakdown?.grand_total || 0),
  );
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  // Monochrome mode: route every colour call through a luminance filter so the
  // entire PDF prints crisply on a B&W laser. We also remap the two near-black
  // dark blocks (header band, grand-total card) to light grey + black text.
  if (monochrome) {
    const hexToRgbTuple = (hex) => {
      const h = String(hex).replace("#", "");
      const full =
        h.length === 3
          ? h.split("").map((c) => c + c).join("")
          : h.padEnd(6, "0").slice(0, 6);
      return [
        parseInt(full.slice(0, 2), 16) || 0,
        parseInt(full.slice(2, 4), 16) || 0,
        parseInt(full.slice(4, 6), 16) || 0,
      ];
    };
    const lumify = (r, g, b) => {
      const lum = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
      const adj = lum > 230 ? 255 : lum < 25 ? 0 : lum;
      return [adj, adj, adj];
    };
    const toGray = (...args) => {
      // No-arg / nullish: leave as-is so jsPDF can throw its own clean error
      if (args.length === 0) return args;
      let r, g, b;
      const a0 = args[0];
      if (args.length >= 3 && typeof a0 === "number") {
        [r, g, b] = args;
      } else if (Array.isArray(a0)) {
        [r, g, b] = a0;
      } else if (typeof a0 === "string") {
        // hex string e.g. "#fff" or named color — fall back to original args
        if (a0.startsWith("#")) {
          [r, g, b] = hexToRgbTuple(a0);
        } else {
          return args; // let jsPDF handle named color natively
        }
      } else if (typeof a0 === "number" && args.length === 1) {
        return [a0, a0, a0];
      } else {
        return args;
      }
      if (r == null || g == null || b == null) return args;
      return lumify(Number(r) || 0, Number(g) || 0, Number(b) || 0);
    };
    const wrap = (fnName) => {
      const orig = doc[fnName].bind(doc);
      doc[fnName] = (...args) => orig(...toGray(...args));
    };
    wrap("setFillColor");
    wrap("setTextColor");
    wrap("setDrawColor");
  }
  const pageW = doc.internal.pageSize.getWidth();
  let y = 0;

  // Header band — solid black on color, white on monochrome (saves toner & looks crisp on B&W)
  if (monochrome) {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageW, 32, "F");
    doc.setDrawColor(40, 40, 40);
    doc.setLineWidth(0.4);
    doc.line(14, 30.5, pageW - 14, 30.5);
  } else {
    doc.setFillColor(10, 12, 16);
    doc.rect(0, 0, pageW, 32, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 30, pageW, 1.5, "F");
  }

  let textX = 14;
  if (logoData) {
    try {
      doc.addImage(logoData, fileExtToPdfFormat(logoData), 14, 6, 14, 14);
      textX = 32;
    } catch (e) {
      console.warn("Skipping bad logo image in PDF header:", e);
    }
  }
  doc.setTextColor(...GOLD);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text(firmName, textX, 15);

  doc.setTextColor(...(monochrome ? [80, 80, 80] : [200, 200, 210]));
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(tagline.toUpperCase(), textX, 21);

  const date = new Date().toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  doc.setTextColor(...(monochrome ? [60, 60, 60] : [180, 180, 190]));
  doc.setFontSize(9);
  doc.text(date, pageW - 14, 15, { align: "right" });
  if (savedId) {
    doc.text(`Ref #${savedId.slice(0, 8)}`, pageW - 14, 20, { align: "right" });
  }

  // "NOT APPROVED" banner for internal (unapproved) downloads.
  const notApproved = Boolean(brandingOpts?.notApproved);
  if (notApproved) {
    const bandTop = 33;
    doc.setFillColor(...(monochrome ? [232, 232, 232] : [178, 34, 34]));
    doc.rect(0, bandTop, pageW, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...(monochrome ? [0, 0, 0] : [255, 255, 255]));
    doc.text(
      "NOT APPROVED  ·  INTERNAL DRAFT  ·  NOT FOR CUSTOMER CIRCULATION",
      pageW / 2,
      bandTop + 4.7,
      { align: "center" },
    );
    y = 48;
  } else {
    y = 42;
  }

  // Firm business info (GST + address) — only when admin enabled it
  const showBiz = Boolean(brandingInfo.show_business_info);
  const biz = {
    gst: (brandingInfo.gst_number || "").trim(),
    address: (brandingInfo.address || "").trim(),
  };
  if (showBiz && (biz.gst || biz.address)) {
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("FROM", 14, y);
    y += 4;
    doc.setTextColor(40, 44, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(firmName, 14, y);
    y += 4.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (biz.gst) {
      doc.setTextColor(...MUTED);
      doc.text("GSTIN", 14, y);
      doc.setTextColor(40, 44, 60);
      doc.text(biz.gst, 30, y);
      y += 4.5;
    }
    if (biz.address) {
      const addrLines = doc.splitTextToSize(biz.address, pageW - 28);
      doc.setTextColor(60, 64, 80);
      doc.text(addrLines, 14, y);
      y += addrLines.length * 4.2;
    }
    y += 3;
  }

  // Customer block
  const c = data.customer || {};
  doc.setTextColor(...DARK);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("Customer", 14, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 64, 80);
  const lines = [
    ["Name", c.name || "-"],
    ["Mobile", c.mobile || "-"],
    ["Location", [c.location, c.city].filter(Boolean).join(", ") || "-"],
    ["Given by", c.quotation_given_by || "-"],
  ];
  lines.forEach(([k, v]) => {
    doc.setTextColor(...MUTED);
    doc.text(k, 14, y);
    doc.setTextColor(40, 44, 60);
    doc.text(String(v), 42, y);
    y += 5;
  });

  y += 3;

  // Rooms & measurements table. Excluded items (is_included === false) are kept
  // in the list but rendered struck-through so the customer sees exactly what
  // they toggled off; rowFlags[i] marks those rows for the strike overlay.
  const rows = [];
  const rowFlags = [];
  (data.rooms || []).forEach((room, ri) => {
    (room.measurements || []).forEach((m, mi) => {
      const area = sqft(m);
      const ex = m.is_included === false;
      const finishLabel =
        (m.finish_name || "-") + (m.is_modular ? " · Modular" : "") + (ex ? "  (excluded)" : "");
      rows.push([
        mi === 0 ? `${ri + 1}. ${room.area_type}${room.description ? " — " + room.description : ""}` : "",
        formatDims(m),
        `${area.toFixed(1)} sqft`,
        finishLabel,
      ]);
      rowFlags.push(ex);
    });
    // Per-room sqft subtotal (modular + non-modular split)
    if ((room.measurements || []).length > 0) {
      rows.push([
        "",
        "",
        { content: `Total: ${formatSqftBreakdown(roomSqftBreakdown(room))}`, colSpan: 2, styles: { fontStyle: "bold", textColor: [120, 95, 60], halign: "right" } },
      ]);
      rowFlags.push(false);
    }
  });
  // Grand total across all rooms
  if ((data.rooms || []).length > 0) {
    rows.push([
      { content: `Grand total area: ${formatSqftBreakdown(roomsSqftBreakdown(data.rooms))}`, colSpan: 4, styles: { fontStyle: "bold", textColor: [20, 24, 40], fillColor: [245, 240, 232], halign: "right" } },
    ]);
    rowFlags.push(false);
  }

  const strikeExcludedRow = (hook, table) => {
    if (hook.section !== "body") return;
    if (!table[hook.row.index]) return;
    const c = hook.cell;
    doc.setDrawColor(150, 150, 150);
    doc.setLineWidth(0.3);
    const yy = c.y + c.height / 2;
    doc.line(c.x + 1, yy, c.x + c.width - 1, yy);
  };
  const dimExcludedRow = (hook, table) => {
    if (hook.section === "body" && table[hook.row.index]) {
      hook.cell.styles.textColor = [150, 150, 150];
    }
  };
  const hasExcluded =
    (data.rooms || []).some((r) => (r.measurements || []).some((m) => m.is_included === false)) ||
    (data.accessories || []).some((a) => (a.quantity || 0) > 0 && a.is_included === false);

  if (rows.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text("Areas & Measurements", 14, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Room", "Size", "Area", "Finish"]],
      body: rows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2.5, textColor: [40, 44, 60] },
      headStyles: {
        fillColor: [245, 240, 232],
        textColor: [120, 95, 60],
        fontStyle: "bold",
        lineWidth: { bottom: 0.4 },
        lineColor: GOLD,
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles: {
        0: { fontStyle: "bold", textColor: [20, 24, 40] },
      },
      margin: { left: 14, right: 14 },
      didParseCell: (hook) => dimExcludedRow(hook, rowFlags),
      didDrawCell: (hook) => strikeExcludedRow(hook, rowFlags),
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Note explaining struck-through items (shown once, near the line items).
  if (hasExcluded) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(...(monochrome ? [110, 110, 110] : [150, 130, 95]));
    doc.text(
      "Struck-through items are excluded from the totals at the customer's request.",
      14,
      y,
    );
    doc.setFont("helvetica", "normal");
    y += 6;
  }
  const tierRows = (data.rooms || []).map((room, ri) => {
    const cells = [
      `${ri + 1}. ${room.area_type}${room.description ? " — " + room.description : ""}`,
    ];
    sortedTierList.forEach((t) => {
      const r = (t.breakdown.rooms || []).find((x) => x.id === room.id);
      cells.push(rupee(r?.area_cost || 0));
    });
    return cells;
  });
  if (tierRows.length > 0 && sortedTierList.length > 0) {
    if (y > 235) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text("Room-wise price by tier", 14, y);
    y += 3;
    const head = ["Room"];
    sortedTierList.forEach((t) => {
      head.push(t.name);
    });
    const columnStyles = {
      0: { fontStyle: "bold", textColor: [20, 24, 40] },
    };
    sortedTierList.forEach((t, i) => {
      columnStyles[i + 1] = {
        halign: "right",
        fontStyle: "bold",
        textColor: tierAccent(t.color, i),
      };
    });
    autoTable(doc, {
      startY: y,
      head: [head],
      body: tierRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2.5, textColor: [40, 44, 60] },
      headStyles: {
        fillColor: [245, 240, 232],
        textColor: [120, 95, 60],
        fontStyle: "bold",
        lineWidth: { bottom: 0.4 },
        lineColor: GOLD,
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles,
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Accessories table (excluded accessories kept but struck-through)
  const validAccessories = (data.accessories || []).filter(
    (a) => (a.quantity || 0) > 0
  );
  const accFlags = validAccessories.map((a) => a.is_included === false);
  if (validAccessories.length > 0) {
    if (y > 240) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text("Accessories", 14, y);
    y += 3;
    autoTable(doc, {
      startY: y,
      head: [["Item", "Qty", "Unit price", "Amount"]],
      body: validAccessories.map((a) => [
        a.name + (a.is_included === false ? "  (excluded)" : ""),
        String(a.quantity),
        rupee(a.price_per_unit),
        rupee(a.price_per_unit * a.quantity),
      ]),
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 2.5, textColor: [40, 44, 60] },
      headStyles: {
        fillColor: [245, 240, 232],
        textColor: [120, 95, 60],
        fontStyle: "bold",
        lineWidth: { bottom: 0.4 },
        lineColor: GOLD,
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      columnStyles: {
        2: { halign: "right" },
        3: { halign: "right", fontStyle: "bold" },
      },
      margin: { left: 14, right: 14 },
      didParseCell: (hook) => dimExcludedRow(hook, accFlags),
      didDrawCell: (hook) => strikeExcludedRow(hook, accFlags),
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  // Remarks
  if (data.remarks) {
    if (y > 250) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text("Remarks", 14, y);
    y += 5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(60, 64, 80);
    const wrapped = doc.splitTextToSize(data.remarks, pageW - 28);
    doc.text(wrapped, 14, y);
    y += wrapped.length * 5 + 5;
  }

  // Grand Totals block (dynamic tiers)
  const firstTier = tierList[0] || null;
  const discount = parseFloat(data.discount_amount) || 0;
  const taxPct = parseFloat(data.tax_percent) || 0;

  if (tierList.length > 0) {
    if (y > 215) {
      doc.addPage();
      y = 20;
    }

    // Shared summary snapshot (uses the first selected tier as reference)
    const ref = firstTier.breakdown;
    const refName = firstTier.name;
    const summaryLines = [
      [`Area (${refName})`, rupee(ref.area_cost)],
      [`Accessories (${refName})`, rupee(ref.accessories_cost)],
      [`Subtotal (${refName})`, rupee(ref.subtotal)],
    ];
    if (discount > 0) summaryLines.push(["Discount (all tiers)", "- " + rupee(discount)]);
    if (taxPct > 0) summaryLines.push(["Tax", `${taxPct}%`]);

    // Pre-compute distinct finishes used in this quote — the chip list is
    // the same set across all tiers; only the per-tier rate changes.
    const distinctFinishes = (() => {
      const seen = new Map(); // finish_id -> { name }
      (data.rooms || []).forEach((room) => {
        (room.measurements || []).forEach((m) => {
          if (!m.finish_id || !m.finish_name) return;
          if (seen.has(m.finish_id)) return;
          seen.set(m.finish_id, { name: m.finish_name });
        });
      });
      return Array.from(seen.values()).map((v, i) => ({
        ...v,
        finish_id: Array.from(seen.keys())[i],
      }));
    })();
    // We need access to each measurement's finish_tier_prices to drive cascade.
    // Build a fast lookup: finish_id -> a representative measurement object.
    const finishMeasurement = new Map();
    (data.rooms || []).forEach((room) => {
      (room.measurements || []).forEach((m) => {
        if (m.finish_id && !finishMeasurement.has(m.finish_id)) {
          finishMeasurement.set(m.finish_id, m);
        }
      });
    });

    // Big tier-total cards: order highest total → lowest (matches the public
    // page). The room-wise table above keeps its original order untouched.
    const sortedTiers = [...tierList].sort(
      (a, z) => (z.breakdown?.grand_total || 0) - (a.breakdown?.grand_total || 0),
    );

    // Pre-render each tier's chip line so we can size the block accurately.
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const tierChipLines = sortedTiers.map((t) => {
      if (distinctFinishes.length === 0) return [];
      const parts = distinctFinishes.map((f) => {
        const m = finishMeasurement.get(f.finish_id);
        const rate = m ? resolveFinishRateV2(m, t, tierList, t.finish_discount) : 0;
        return `${f.name} ${rupee(rate)}`;
      });
      return doc.splitTextToSize(parts.join("   ·   "), pageW - 40);
    });

    // Total height = per-tier total line + (wrapped chip rows × 4mm) + 1.5mm gap.
    const totalLinesH = sortedTiers.reduce((acc, _t, i) => {
      const totalRow = i === 0 ? 7 : 6.5;
      const chipH = (tierChipLines[i]?.length || 0) * 4 + (tierChipLines[i]?.length ? 1.5 : 0);
      return acc + totalRow + chipH;
    }, 0);
    const blockH = summaryLines.length * 6 + 18 + totalLinesH;
    if (monochrome) {
      doc.setFillColor(248, 248, 248);
      doc.roundedRect(14, y, pageW - 28, blockH, 3, 3, "F");
      doc.setDrawColor(60, 60, 60);
      doc.setLineWidth(0.4);
      doc.roundedRect(14, y, pageW - 28, blockH, 3, 3, "S");
    } else {
      doc.setFillColor(15, 18, 26);
      doc.roundedRect(14, y, pageW - 28, blockH, 3, 3, "F");
      doc.setDrawColor(...GOLD);
      doc.setLineWidth(0.3);
      doc.roundedRect(14, y, pageW - 28, blockH, 3, 3, "S");
    }

    let ly = y + 7;
    summaryLines.forEach(([k, v]) => {
      doc.setTextColor(...(monochrome ? [80, 80, 80] : [180, 180, 190]));
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.text(k, 20, ly);
      doc.setTextColor(...(monochrome ? [20, 20, 20] : [235, 235, 240]));
      doc.text(v, pageW - 20, ly, { align: "right" });
      ly += 6;
    });
    ly += 1;
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.2);
    doc.line(20, ly, pageW - 20, ly);
    ly += 7;

    sortedTiers.forEach((t, i) => {
      const col = tierAccent(t.color, i);
      doc.setTextColor(...col);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(i === 0 ? 10 : 9.5);
      const label = `${t.name.toUpperCase()} TOTAL`;
      doc.text(label, 20, ly);
      doc.setFontSize(i === 0 ? 15 : i === 1 ? 13 : 11.5);
      doc.text(rupee(t.breakdown.grand_total), pageW - 20, ly, { align: "right" });
      ly += i === 0 ? 7 : 6.5;

      // Per-tier finish chips (mirrors the screen UI hero card).
      const lines = tierChipLines[i] || [];
      if (lines.length > 0) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(...col);
        lines.forEach((ln, idx) => {
          doc.text(ln, 20, ly + idx * 4);
        });
        ly += lines.length * 4 + 1.5;
      }
    });

    y += blockH + 8;
  }

  // Payment details block — only when enabled
  if (
    showPayment &&
    (brandingInfo.bank_account_number ||
      brandingInfo.upi_id ||
      qrData)
  ) {
    const hasQr = Boolean(qrData);
    const bankLines = [];
    if (brandingInfo.bank_account_name) bankLines.push(["Name", brandingInfo.bank_account_name]);
    if (brandingInfo.bank_name) bankLines.push(["Bank", brandingInfo.bank_name]);
    if (brandingInfo.bank_account_number) bankLines.push(["A/c", String(brandingInfo.bank_account_number)]);
    if (brandingInfo.ifsc_code) bankLines.push(["IFSC", brandingInfo.ifsc_code]);
    const upiLine = brandingInfo.upi_id ? String(brandingInfo.upi_id) : "";
    const leftRows = bankLines.length + (upiLine ? 1 : 0) + (bankLines.length && upiLine ? 1 : 0);
    const payH = Math.max(hasQr ? 40 : 0, 14 + leftRows * 5);
    if (y + payH > 275) {
      doc.addPage();
      y = 20;
    }
    doc.setFillColor(245, 240, 232);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.roundedRect(14, y, pageW - 28, payH + 6, 3, 3, "FD");
    doc.setTextColor(...GOLD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text("PAYMENT DETAILS", 20, y + 6);

    let py = y + 12;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    if (bankLines.length) {
      doc.setTextColor(120, 95, 60);
      doc.setFont("helvetica", "bold");
      doc.text("Bank transfer", 20, py);
      py += 4.5;
      doc.setFont("helvetica", "normal");
      bankLines.forEach(([k, v]) => {
        doc.setTextColor(140, 120, 90);
        doc.text(k, 20, py);
        doc.setTextColor(40, 44, 60);
        doc.text(String(v), 36, py);
        py += 4.5;
      });
    }
    if (upiLine) {
      py += 1;
      doc.setTextColor(120, 95, 60);
      doc.setFont("helvetica", "bold");
      doc.text("UPI", 20, py);
      py += 4.5;
      doc.setTextColor(40, 44, 60);
      doc.setFont("helvetica", "normal");
      doc.text(upiLine, 20, py);
    }

    if (hasQr) {
      try {
        const qrSize = 34;
        const qrX = pageW - 14 - qrSize - 2;
        const qrY = y + 4;
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(qrX - 2, qrY - 2, qrSize + 4, qrSize + 4, 1, 1, "F");
        doc.addImage(qrData, fileExtToPdfFormat(qrData), qrX, qrY, qrSize, qrSize);
        doc.setTextColor(120, 95, 60);
        doc.setFontSize(7.5);
        doc.text("Scan to pay", qrX + qrSize / 2, qrY + qrSize + 4, { align: "center" });
      } catch (e) {
        console.warn("Skipping bad QR image in PDF payment block:", e);
      }
    }

    y += payH + 12;
  }

  // Validity notice — clearly visible at end of quotation
  if (y > 270) {
    doc.addPage();
    y = 20;
  }
  const validityDays = Math.max(1, parseInt(brandingInfo.validity_days, 10) || 15);
  doc.setFillColor(245, 240, 232);
  doc.setDrawColor(...GOLD);
  doc.setLineWidth(0.3);
  doc.roundedRect(14, y, pageW - 28, 10, 2, 2, "FD");
  doc.setTextColor(120, 95, 60);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.text(
    `This quotation is valid for ${validityDays} ${validityDays === 1 ? "day" : "days"} only from the date of issue.`,
    pageW / 2,
    y + 6.5,
    { align: "center" }
  );
  y += 14;

  // Payment plan (admin-configurable, attached per quotation)
  const plan = data?.payment_plan;
  if (plan && Array.isArray(plan.milestones) && plan.milestones.length) {
    const headerH = 10;
    const rowH = 7;
    const bodyH = plan.milestones.length * rowH + 6;
    const totalH = headerH + bodyH + (plan.description ? 6 : 0);
    if (y + totalH > 282) {
      doc.addPage();
      y = 20;
    }
    doc.setFillColor(248, 244, 236);
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.3);
    doc.roundedRect(14, y, pageW - 28, totalH, 3, 3, "FD");
    doc.setTextColor(120, 95, 60);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(`PAYMENT PLAN · ${String(plan.name || "").toUpperCase()}`, 20, y + 6);
    let py = y + headerH + 2;
    if (plan.description) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(100, 95, 110);
      doc.text(
        doc.splitTextToSize(plan.description, pageW - 40),
        20,
        py,
      );
      py += 5;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    plan.milestones.forEach((m) => {
      const pct = parseFloat(m.percent) || 0;
      doc.setTextColor(120, 95, 60);
      doc.setFont("helvetica", "bold");
      doc.text(`${pct}%`, 20, py);
      doc.setTextColor(40, 44, 60);
      doc.setFont("helvetica", "normal");
      doc.text(String(m.label || ""), 34, py);
      py += rowH;
    });
    y += totalH + 4;
  }

  // Footer note (admin-configurable)
  const footerNote = (brandingInfo.footer_note || "").trim();
  if (footerNote) {
    const wrappedNote = doc.splitTextToSize(footerNote, pageW - 36);
    const noteH = wrappedNote.length * 4.5 + 8;
    if (y + noteH > 282) {
      doc.addPage();
      y = 20;
    }
    doc.setFillColor(248, 248, 250);
    doc.setDrawColor(210, 210, 215);
    doc.setLineWidth(0.2);
    doc.roundedRect(14, y, pageW - 28, noteH, 2, 2, "FD");
    doc.setTextColor(140, 120, 95);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("NOTE", 20, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(60, 64, 80);
    doc.text(wrappedNote, 20, y + 10);
    y += noteH + 4;
  }

  // Footer
  if (shareUrl) {
    if (y > 280) {
      doc.addPage();
      y = 20;
    }
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Quotation link:", 14, y);
    doc.setTextColor(100, 100, 160);
    doc.textWithLink(shareUrl, 40, y, { url: shareUrl });
  }

  // Per-page footer: hairline + firm (left) + page number (right).
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.line(14, 286, pageW - 14, 286);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 160);
    doc.text(`Generated by ${firmName}`, 14, 291);
    doc.text(`Page ${p} of ${totalPages}`, pageW - 14, 291, { align: "right" });
  }

  const safeName = (c.name || "customer").replace(/[^a-z0-9]+/gi, "_").slice(0, 24);
  const fileFirm = firmName.replace(/[^a-z0-9]+/gi, "_").slice(0, 20) || "Quotation";
  doc.save(`${fileFirm}_${safeName}_${date.replace(/ /g, "-")}${fileSuffix}.pdf`);
}

// Orchestrates dual download: pixel-perfect color copy of the public link +
// printer-friendly grayscale vector PDF. The two downloads are kicked off
// sequentially so the browser doesn't suppress the second save.
export async function exportQuotationDualPDFs(data, savedId, shareUrl, tierSettings, brandingOpts) {
  const brandingInfo = brandingOpts?.branding || {};
  const firmName = (brandingInfo.firm_name || "LIVANA").toUpperCase();
  const fileFirm = firmName.replace(/[^a-z0-9]+/gi, "_").slice(0, 20) || "Quotation";
  const customerName = data?.customer?.name || "customer";
  const safeName = customerName.replace(/[^a-z0-9]+/gi, "_").slice(0, 24);
  const dateLabel = new Date()
    .toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    .replace(/ /g, "-");

  const errors = [];
  const approved = brandingOpts?.approved !== false; // default true (approved) unless explicitly false
  const notApproved = !approved;

  // 1. Color "exact copy" PDF — the public-page snapshot is only used for an
  //    APPROVED quote (its public link is live). For unsaved/preview OR
  //    not-yet-approved quotes we render the colour vector PDF from local data,
  //    stamped "NOT APPROVED" when applicable.
  if (savedId && shareUrl && approved) {
    const colorFile = `${fileFirm}_${safeName}_${dateLabel}_color.pdf`;
    try {
      await exportPublicSnapshotPDF(shareUrl, colorFile);
    } catch (e) {
      errors.push(["color", e]);
      // Fallback: vector colour PDF so the user still gets a colour version
      try {
        await exportQuotationPDF(data, savedId, shareUrl, tierSettings, {
          ...brandingOpts,
          monochrome: false,
          fileSuffix: "_color",
        });
      } catch (e2) {
        errors.push(["color-fallback", e2]);
      }
    }
  } else {
    try {
      await exportQuotationPDF(data, savedId, shareUrl, tierSettings, {
        ...brandingOpts,
        monochrome: false,
        fileSuffix: "_color",
        notApproved,
      });
    } catch (e) {
      errors.push(["color-vector", e]);
    }
  }

  // 2. Black & white print PDF — slight delay so the second download isn't
  //    blocked by the browser's same-frame protection.
  await new Promise((r) => setTimeout(r, 350));
  try {
    await exportQuotationPDF(data, savedId, shareUrl, tierSettings, {
      ...brandingOpts,
      monochrome: true,
      fileSuffix: "_print",
      notApproved,
    });
  } catch (e) {
    errors.push(["print", e]);
  }

  if (errors.length) {
    errors.forEach(([tag, err]) => {
      console.error(`PDF dual export [${tag}]:`, err && err.message ? err.message : err, err && err.stack ? err.stack : "");
    });
    if (errors.length >= 2) throw new Error("Both PDF exports failed");
  }
}
